import {
  BitkitButton,
  BitkitIconButton,
  BitkitSpinner,
  IconArrowDown,
  IconArrowUp,
  IconBoxLinesWrap,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import useAIAgentTaskShow from '@/client/BFF/Workspaces/Projects/AIAgentTasks/useAIAgentTaskShow';
import EditDialog from '@/components/Dialog/EditDialog';
import FormattedLog from '@/components/LogViewer/old/FormattedLog';
import { PublicApiLog } from '@/models/Log';
import { PublicApiLogResponse } from '@/models/network';
import useExpiringRawLogUrl from '@/pages/BuildDetailsPage/hooks/useExpiringRawLogUrl';
import { publicApi } from '@/utils/api';

export type AIChatLogDialogTurnInfo = {
  id: string;
  userMessage: string;
  appSlug: string;
  isTaskPath?: boolean;
};

type SharedLogViewerProps = {
  logData: string | undefined;
  url: string | undefined;
};

const SharedLogViewer = ({ logData, url }: SharedLogViewerProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [wrap, setWrap] = useState(true);

  return (
    <>
      {(!url || !logData) && (
        <BitkitSpinner display="block" marginInline="auto" width="48" height="48" role="status" aria-label="Loading" />
      )}
      {!url && (
        <Text textStyle="body/md/regular" color="text/secondary" textAlign="center">
          Waiting for complete log...
        </Text>
      )}
      {logData && (
        <Box borderRadius="8" background="background/fixed-contrast" overflowY="hidden" position="relative">
          <Box ref={scrollRef} overflow="auto" maxHeight="max(30rem, 50vh)">
            <FormattedLog
              whiteSpace={wrap ? 'unset' : 'nowrap'}
              fontSize="14px"
              lineHeight="20px"
              padding="32"
              messages={[{ message: logData }]}
            />
          </Box>
          <Box
            padding="8"
            display="flex"
            flexDirection="row"
            gap="8"
            position="absolute"
            insetInlineEnd="16"
            insetBlockEnd="8"
          >
            <BitkitButton variant="secondary" icon={IconBoxLinesWrap} onClick={() => setWrap((w) => !w)} size="sm">
              Wrap
            </BitkitButton>
            <BitkitIconButton
              variant="secondary"
              icon={IconArrowUp}
              label="Scroll to top"
              onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              size="sm"
            />
            <BitkitIconButton
              variant="secondary"
              icon={IconArrowDown}
              label="Scroll to bottom"
              size="sm"
              onClick={() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

const AIChatBuildLogDialogContent = ({ turn }: { turn: AIChatLogDialogTurnInfo }) => {
  const { url } = useExpiringRawLogUrl(turn.appSlug, turn.id, true, false);
  const { data: logData } = useQuery({
    queryKey: ['apps', turn.appSlug, 'builds', turn.id, 'full-log', url],
    enabled: Boolean(url),
    queryFn: () => (url ? fetch(url).then((res) => res.text()) : Promise.reject('no url')),
  });

  return <SharedLogViewer logData={logData} url={url} />;
};

const AIChatTaskLogDialogContent = ({ turn }: { turn: AIChatLogDialogTurnInfo }) => {
  const { data: task } = useAIAgentTaskShow({ appSlug: turn.appSlug, taskId: turn.id });

  const { data: rawLogUrl } = useQuery({
    queryKey: ['apps', turn.appSlug, 'tasks', task?.taskId, 'log', 'raw-url'],
    enabled: Boolean(task?.taskId),
    queryFn: async () => {
      const { data } = await publicApi.get<PublicApiLogResponse<PublicApiLog>>(
        `/v0.1/apps/${turn.appSlug}/tasks/${task?.taskId}/log`,
      );
      return data.expiringRawLogUrl;
    },
    refetchInterval: (query) => (query.state.data ? false : 5000),
  });

  const { data: logData } = useQuery({
    queryKey: ['apps', turn.appSlug, 'tasks', task?.taskId, 'full-log', rawLogUrl],
    enabled: Boolean(rawLogUrl),
    queryFn: () => (rawLogUrl ? fetch(rawLogUrl).then((res) => res.text()) : Promise.reject('no url')),
  });

  return <SharedLogViewer logData={logData} url={rawLogUrl} />;
};

const AIChatLogDialogContent = ({ turn }: { turn: AIChatLogDialogTurnInfo }) => {
  if (turn.isTaskPath) {
    return <AIChatTaskLogDialogContent turn={turn} />;
  }
  return <AIChatBuildLogDialogContent turn={turn} />;
};

const AIChatLogDialog = ({ turn, onClose }: { turn: AIChatLogDialogTurnInfo | undefined; onClose: () => void }) => {
  return (
    <EditDialog
      editedValue={turn}
      title={turn ? `${turn.userMessage} - logs` : ''}
      onClose={onClose}
      footerButtons={(_value, close) => (
        <BitkitButton onClick={close} variant="secondary">
          Close
        </BitkitButton>
      )}
    >
      {(value) => <AIChatLogDialogContent turn={value} />}
    </EditDialog>
  );
};

export default AIChatLogDialog;
